import { getMimeType } from "./utils/metaParser";
import { parsePlaylist, IPlaylist, IChunk } from "./utils/playlistParser";
import { isSupported } from "./utils/isSupported";
import { createMediaSourceSession, IMediaSourceSession } from "./utils/mediaSourceSession";

interface IOptions {
  bufferLength: number;
}

interface IBufferedRange {
  startTime: number;
  endTime: number;
}

const BUFFER_LENGTH = 4;

export class HLS {
  private session?: IMediaSourceSession;
  private mediaSource?: MediaSource;
  private element?: HTMLVideoElement;
  private src?: string;
  private playlist?: IPlaylist;
  private readonly options: IOptions;
  private chunks: IChunk[] = [];
  private loadingChunks: IChunk[] = [];
  private currentChunk?: IChunk;
  private bufferedRanges?: IBufferedRange[];
  private canClose: boolean = false;
  private streamingPaused: boolean = false;

  public constructor(params?: IOptions) {
    this.options = params || {
      bufferLength: BUFFER_LENGTH
    };

    if (params && params.bufferLength) {
      this.options.bufferLength = params.bufferLength;
    }
  }

  public static isSupported() {
    return isSupported();
  }

  public load(src: string, element: HTMLVideoElement) {
    this.element = element;
    this.src = src;

    const session = createMediaSourceSession();

    if (!session) {
      return;
    }

    this.session = session;
    this.mediaSource = session.mediaSource;
    this.streamingPaused = false;

    session.mediaSource.addEventListener("sourceopen", this.sourceOpen);
    session.onStreamingChange(this.streamingChange);
    session.attach(element);
  }

  public destroy() {
    if (this.mediaSource && this.mediaSource.sourceBuffers.length > 0) {
      this.mediaSource.removeSourceBuffer(this.mediaSource.sourceBuffers[0]);
    }

    if (this.element) {
      this.element.removeEventListener("timeupdate", this.eventListener);
      this.element.removeEventListener("seeking", this.eventListener);
    }

    this.session?.detach();

    this.session = undefined;
    this.mediaSource = undefined;
    this.element = undefined;
    this.playlist = undefined;
    this.bufferedRanges = undefined;
  }

  private streamingChange = (streaming: boolean) => {
    this.streamingPaused = !streaming;

    if (streaming && this.mediaSource?.sourceBuffers[0]) {
      this.loadChunks();
    }
  };

  private async loadPlaylist(src: string) {
    const response = await fetch(src);

    return await response.text();
  }

  private sourceOpen = async () => {
    this.mediaSource?.removeEventListener("sourceopen", this.sourceOpen);

    if (!this.src) {
      return;
    }

    const content = await this.loadPlaylist(this.src);
    this.playlist = parsePlaylist(content);
    this.chunks = [...this.playlist.chunks];
    this.bufferedRanges = [];
    this.canClose = false;

    if (this.mediaSource) {
      this.mediaSource.duration = this.playlist.duration;
    }

    const initBuffer = await this.fetchChunk(
      this.playlist.metaChunk.src,
      this.playlist.metaChunk.byteRange.start,
      this.playlist.metaChunk.byteRange.end,
    );

    const mimeType = getMimeType(initBuffer);

    if (!this.mediaSource) {
      return;
    }

    const sourceBuffer = this.mediaSource.addSourceBuffer(mimeType);

    sourceBuffer.onupdateend = () => {
      if (this.bufferedRanges && this.bufferedRanges.length > 0 && this.mediaSource && this.currentChunk && this.playlist) {
        const lastChunk = this.playlist.chunks[this.playlist.chunks.length - 1];

        if (lastChunk.index === this.currentChunk.index) {
          this.canClose = true;
        }
      }

      if (this.canClose && this.mediaSource && this.mediaSource.readyState === "open") {
        this.mediaSource.endOfStream();
      }

      this.updateBufferedRanges();

      this.currentChunk = undefined;

      if (!this.mediaSource) {
        return;
      }

      if (this.chunks.length === 0 && this.mediaSource.readyState === "open") {
        this.mediaSource.endOfStream();
        this.element?.removeEventListener("timeupdate", this.eventListener);
        this.element?.removeEventListener("seeking", this.eventListener);
        return;
      }

      this.loadNextChunk();
    };

    this.updateLoadingChunks();

    this.appendBuffer(initBuffer);

    this.element?.addEventListener("timeupdate", this.eventListener);
    this.element?.addEventListener("seeking", this.eventListener);
  };

  private eventListener = () => {
    if (this.mediaSource?.sourceBuffers[0]) {
      this.loadChunks();
    }
  };

  private getSourceBuffer = () => {
    if (this.mediaSource && this.mediaSource.sourceBuffers.length > 0) {
      return this.mediaSource.sourceBuffers[0];
    }
  };

  private appendBuffer = (buffer: ArrayBuffer) => {
    const sourceBuffer = this.getSourceBuffer();

    if (!sourceBuffer) {
      return;
    }

    sourceBuffer.appendBuffer(buffer);
  };

  private async fetchChunk(src: string, start: number, end: number) {
    const response = await fetch(src, { headers: { "Range": `bytes=${start}-${end}` } });
    return await response.arrayBuffer();
  }

  private loadChunks() {
    this.updateLoadingChunks();

    if (!this.currentChunk) {
      this.loadNextChunk();
    }
  }

  private loadNextChunk = async() => {
    if (this.streamingPaused || this.getSourceBuffer()?.updating) {
      return;
    }

    const chunk = this.loadingChunks.shift();

    if (!chunk) {
      return;
    }

    this.currentChunk = chunk;

    const foundIndex = this.chunks.findIndex((chunk) => {
      if (!this.currentChunk) {
        return false;
      }

      return chunk.index === this.currentChunk.index;
    });

    if (this.chunks[foundIndex]) {
      this.chunks.splice(foundIndex, 1);
    }

    const buffer = await this.fetchChunk(chunk.src, chunk.byteRange.start, chunk.byteRange.end);
    this.appendBuffer(buffer);
  };

  private updateLoadingChunks = () => {
    this.loadingChunks = this.getChunksForLoading();
  };

  private getChunksForLoading = () => {
    const currentTime = this.element?.currentTime || 0;
    const leftOverTime = this.getLeftoverTime(currentTime);

    const chunksForLoading: IChunk[] = [];

    if (leftOverTime < this.options.bufferLength) {
      const time = currentTime + leftOverTime;
      const needToLoadTime = this.options.bufferLength - leftOverTime;

      this.chunks.forEach((chunk) => {
        if (time > chunk.endTime) {
          return;
        }

        if (time + needToLoadTime < chunk.startTime) {
          return;
        }

        chunksForLoading.push(chunk);
      });
    }

    return chunksForLoading;
  };

  private getLeftoverTime = (currentTime: number) => {
    if (!this.bufferedRanges) {
      return 0;
    }

    if (this.bufferedRanges.length === 0) {
      return 0;
    }

    const bufferedTime = this.bufferedRanges[0].endTime;

    const time = bufferedTime - currentTime;

    return time > 0 ? time : 0;
  };

  private updateBufferedRanges = () => {
    if (this.currentChunk === undefined || this.bufferedRanges === undefined) {
      return;
    }

    if (this.bufferedRanges.length === 0) {
      this.bufferedRanges.push({
        startTime: this.currentChunk.startTime,
        endTime: this.currentChunk.endTime,
      });

      return;
    }

    const currentChunk = this.currentChunk;

    const index = this.bufferedRanges.findIndex((range) => {
      return range.endTime === currentChunk.startTime;
    });

    if (index === -1) {
      this.bufferedRanges.push({
        startTime: this.currentChunk.startTime,
        endTime: this.currentChunk.endTime,
      });

      return;
    }

    this.bufferedRanges[index].endTime = currentChunk.endTime;

    const mergeIndex = this.bufferedRanges.findIndex(range => {
      return range.startTime === currentChunk.endTime;
    });

    if (mergeIndex !== -1) {
      this.bufferedRanges[index].endTime = this.bufferedRanges[mergeIndex].endTime;
      this.bufferedRanges.splice(mergeIndex, 1);
    }

    const mergePreviousIndex = this.bufferedRanges.findIndex(range => {
      return range.startTime === currentChunk.endTime;
    });

    if (mergePreviousIndex !== -1) {
      this.bufferedRanges[index].startTime = this.bufferedRanges[mergePreviousIndex].startTime;
      this.bufferedRanges.splice(mergePreviousIndex, 1);
    }
  };
}
